import { ActionIcon, Tooltip } from '@mantine/core'
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react'
import type { Coverage } from '@medplum/fhirtypes'
import type { CoverageTemplate, InsuranceRank, LocalCoverage } from '@/types/insuranceTypes'
import type { InsuranceDisplayData } from './manageCoverages.types'

// Each rank is one row in the insurance list: an ordinal chip, a title, and a
// hint line. Only the primary payer is required, so it carries the accent chip.
const RANK_META: Record<InsuranceRank, { ordinal: number; hint: string }> = {
  Primary: { ordinal: 1, hint: 'Main payer for this patient' },
  Secondary: { ordinal: 2, hint: 'Optional' },
  Tertiary: { ordinal: 3, hint: 'Optional' },
}

type ManageCoverageCardProps = {
  type: InsuranceRank
  coverage: LocalCoverage | null
  onEdit: () => void
  onDelete: () => void
  getInsuranceDisplayData: (coverage: Coverage) => InsuranceDisplayData
}

export function ManageCoverageCard({
  type,
  coverage,
  onEdit,
  onDelete,
  getInsuranceDisplayData,
}: ManageCoverageCardProps) {
  const { ordinal, hint } = RANK_META[type]

  // Under the title: the attached payer and its identifiers, or the static hint
  // when no coverage is attached yet.
  let payerName = ''
  let identifiers = ''
  let isPending = false

  if (coverage) {
    let payerId: string | undefined
    let memberId: string | undefined
    let group: string | undefined

    if (coverage.type === 'pending') {
      const template = coverage.data as CoverageTemplate
      isPending = true
      payerName = template.payorName
      payerId = template.payorId
      memberId = template.memberId
      group = template.groupId
    } else {
      const display = getInsuranceDisplayData(coverage.data as Coverage)
      payerName = display.payorName
      payerId = display.payerId
      memberId = display.memberId
      group = display.group
    }

    // Keep every identifier visible with an N/A fallback, as the previous card did.
    identifiers = [
      `Payer ID ${payerId || 'N/A'}`,
      `Member ID ${memberId || 'N/A'}`,
      `Group ID ${group || 'N/A'}`,
    ].join(' · ')
  }

  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
          type === 'Primary' ? 'bg-[#FDECE8] text-[#E5533D]' : 'bg-[#F1F3F5] text-[#697386]'
        }`}
      >
        {ordinal}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13.5px] font-bold text-[#1F2733]">{type} insurance</span>
          {isPending && (
            <span className="rounded-full bg-[#E7EEFB] px-2 py-[1px] text-[10px] font-bold text-[#2C5FE0]">
              Pending
            </span>
          )}
        </div>
        {coverage ? (
          <>
            <div className="truncate text-[12px] font-semibold text-[#4A5160]">{payerName}</div>
            <div className="truncate text-[11.5px] text-[#8A929C]">{identifiers}</div>
          </>
        ) : (
          <div className="truncate text-[12px] text-[#8A929C]">{hint}</div>
        )}
      </div>

      {coverage ? (
        <div className="flex shrink-0 items-center gap-1">
          <Tooltip label="Edit">
            <ActionIcon variant="subtle" size="sm" onClick={onEdit} aria-label="Edit insurance">
              <IconEdit size={15} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete">
            <ActionIcon
              variant="subtle"
              size="sm"
              color="red"
              onClick={onDelete}
              aria-label="Delete insurance"
            >
              <IconTrash size={15} />
            </ActionIcon>
          </Tooltip>
        </div>
      ) : (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-[#D8DBE0] bg-white px-3 py-[6px] text-[12.5px] font-bold text-[#33383F] hover:bg-[#F7F8FA]"
        >
          <IconPlus size={14} stroke={2.4} />
          Add
        </button>
      )}
    </div>
  )
}
